import * as React from "react"
import { cn } from "@/shared/lib/utils"

interface OTPInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  autoFocus?: boolean
}

/**
 * OTP 验证码输入组件
 * 每个数字一个小方框，支持粘贴、自动跳转
 */
export function OTPInput({
  length = 6,
  value,
  onChange,
  disabled = false,
  className,
  autoFocus = false,
}: OTPInputProps) {
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([])

  // 将 value 拆分成数组
  const valueArray = React.useMemo(() => {
    const arr = value.split("").slice(0, length)
    // 补齐空位
    while (arr.length < length) {
      arr.push("")
    }
    return arr
  }, [value, length])

  // 更新值
  const updateValue = (newValueArray: string[]) => {
    const newValue = newValueArray.join("").slice(0, length)
    onChange(newValue)
  }

  // 处理输入
  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // 只允许数字
    const digit = inputValue.replace(/\D/g, "").slice(-1)

    if (digit) {
      const newValueArray = [...valueArray]
      newValueArray[index] = digit
      updateValue(newValueArray)

      // 自动跳转到下一个
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  // 处理按键
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault()
      const newValueArray = [...valueArray]

      if (valueArray[index]) {
        // 如果当前格有值，清空当前格
        newValueArray[index] = ""
        updateValue(newValueArray)
      } else if (index > 0) {
        // 如果当前格为空，清空上一格并跳转
        newValueArray[index - 1] = ""
        updateValue(newValueArray)
        inputRefs.current[index - 1]?.focus()
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault()
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault()
      inputRefs.current[index + 1]?.focus()
    }
  }

  // 处理粘贴
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("text")
    // 只保留数字，去除空格和其他字符
    const digits = pastedData.replace(/\D/g, "").slice(0, length)

    if (digits) {
      const newValueArray = digits.split("")
      // 补齐空位
      while (newValueArray.length < length) {
        newValueArray.push("")
      }
      updateValue(newValueArray)

      // 聚焦到最后一个输入的位置或最后一格
      const focusIndex = Math.min(digits.length, length - 1)
      inputRefs.current[focusIndex]?.focus()
    }
  }

  // 处理聚焦 - 选中当前格内容
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }

  return (
    <div
      className={cn("flex items-center justify-center gap-2", className)}
      onPaste={handlePaste}
    >
      {valueArray.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={handleFocus}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          className={cn(
            "w-11 h-12 text-center text-xl font-semibold",
            "rounded-lg border-2 border-slate-200 dark:border-slate-700",
            "bg-white dark:bg-slate-900",
            "text-slate-900 dark:text-slate-100",
            "focus:border-primary focus:ring-2 focus:ring-primary/20",
            "outline-none transition-all duration-200",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            digit && "border-primary/50 bg-primary/5"
          )}
          aria-label={`验证码第 ${index + 1} 位`}
        />
      ))}
    </div>
  )
}

